import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  PushDestination,
  PushServiceType,
} from '../../shared/interfaces/push-destination.interface';
import { PushDestinationFormStore } from './push-destination-form.store';
import {
  PUSH_EVENT_TYPES,
  PUSH_SERVICE_TYPES,
  ServiceTypeOption,
  buildCreateRequest,
  buildUpdateRequest,
  destinationToFormValues,
  selectedEventsFromForm,
} from './push-destination-form.mapper';
import {
  emailListValidator,
  httpsValidator,
} from './push-destination-form.validators';

export interface PushDestinationDialogData {
  projectId: number;
  mode: 'create' | 'edit';
  destination?: PushDestination;
}

/**
 * Push-destination create/edit dialog.
 *
 * A thin view over {@link PushDestinationFormStore}: the store owns deployment
 * capabilities (`emailEnabled`, via `resource()`) and the save calls, while this
 * component owns the reactive FormGroup and delegates all form ⇄ request
 * shaping to the pure {@link buildCreateRequest}/{@link buildUpdateRequest}
 * mapper and the pure {@link httpsValidator}/{@link emailListValidator}
 * validators. Mirrors the add-connection dialog modernization (SCRUM-760).
 */
@Component({
  selector: 'app-push-destination-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './push-destination-dialog.component.html',
  styleUrls: ['./push-destination-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PushDestinationFormStore],
})
export class PushDestinationDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(PushDestinationFormStore);
  dialogRef = inject<MatDialogRef<PushDestinationDialogComponent>>(MatDialogRef);
  data = inject<PushDestinationDialogData>(MAT_DIALOG_DATA);

  form!: FormGroup;
  isSaving = signal(false);
  error = signal<string | null>(null);
  showCredential = signal(false);
  /** Whether the deployment has SMTP configured; gates the email service type. */
  readonly emailEnabled = this.store.emailEnabled;

  readonly serviceTypes: ServiceTypeOption[] = PUSH_SERVICE_TYPES;
  readonly eventTypes = PUSH_EVENT_TYPES;

  get isEditMode(): boolean {
    return this.data.mode === 'edit';
  }

  get dialogTitle(): string {
    return this.isEditMode ? 'Edit Push Destination' : 'Add Push Destination';
  }

  get saveButtonText(): string {
    if (this.isSaving()) return this.isEditMode ? 'Saving...' : 'Adding...';
    return this.isEditMode ? 'Save Changes' : 'Add Destination';
  }

  get selectedServiceType(): PushServiceType | null {
    return this.form?.get('serviceType')?.value || null;
  }

  get urlHint(): string {
    const option = this.serviceTypes.find(
      (s) => s.value === this.selectedServiceType,
    );
    return option?.hint || 'Enter webhook URL';
  }

  get isGeneric(): boolean {
    return this.selectedServiceType === 'generic';
  }

  get isEmail(): boolean {
    return this.selectedServiceType === 'email';
  }

  /**
   * Service types offered in the picker. Email is hidden unless the deployment
   * has SMTP configured — except in edit mode for an existing email destination,
   * so it always renders the card for the type being edited.
   */
  get availableServiceTypes(): ServiceTypeOption[] {
    return this.serviceTypes.filter(
      (st) =>
        st.value !== 'email' ||
        this.emailEnabled() ||
        (this.isEditMode && this.data.destination?.serviceType === 'email'),
    );
  }

  get selectedEvents(): string[] {
    if (!this.form) return [];
    return selectedEventsFromForm(this.form.getRawValue());
  }

  get canSave(): boolean {
    if (!this.form) return false;
    return this.form.valid && this.selectedEvents.length > 0 && !this.isSaving();
  }

  ngOnInit(): void {
    this.buildForm();
    if (this.isEditMode && this.data.destination) {
      this.populateForm(this.data.destination);
    }
    this.store.init(this.data.projectId);
  }

  private buildForm(): void {
    const formConfig: Record<string, unknown> = {
      serviceType: [
        { value: '', disabled: this.isEditMode },
        Validators.required,
      ],
      name: ['', Validators.required],
      url: ['', [Validators.required, httpsValidator]],
      emailTo: [''],
      emailReplyTo: [''],
      scope: ['project'],
      credential: [''],
      enabled: [true],
    };

    for (const et of this.eventTypes) {
      // Job-lifecycle events default on; Manual (script notifications) is opt-in.
      formConfig[`event_${et.value}`] = [et.value !== 'Manual'];
    }

    this.form = this.fb.group(formConfig);

    this.form
      .get('serviceType')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: PushServiceType) => {
        this.error.set(null);
        this.applyTypeValidators(type);
      });
  }

  /**
   * Swap validators between the webhook `url` field and the email recipient
   * fields depending on the selected service type, so only the active fields
   * gate `form.valid`.
   */
  private applyTypeValidators(type: PushServiceType | ''): void {
    const url = this.form.get('url')!;
    const emailTo = this.form.get('emailTo')!;
    if (type === 'email') {
      url.clearValidators();
      emailTo.setValidators([Validators.required, emailListValidator]);
    } else {
      url.setValidators([Validators.required, httpsValidator]);
      emailTo.clearValidators();
    }
    url.updateValueAndValidity({ emitEvent: false });
    emailTo.updateValueAndValidity({ emitEvent: false });
  }

  private populateForm(dest: PushDestination): void {
    this.form.patchValue(destinationToFormValues(dest));
    // Service type is disabled in edit mode, so valueChanges won't fire — apply
    // the matching validators directly.
    this.applyTypeValidators(dest.serviceType);
  }

  toggleCredentialVisibility(): void {
    this.showCredential.update((v) => !v);
  }

  onSave(): void {
    if (!this.canSave) return;
    this.error.set(null);
    this.isSaving.set(true);

    const form = this.form.getRawValue();
    const save$ =
      this.isEditMode && this.data.destination
        ? this.store.update(
            this.data.destination.destinationId,
            buildUpdateRequest(form),
          )
        : this.store.create(buildCreateRequest(form));

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (destination) => {
        this.isSaving.set(false);
        this.dialogRef.close({ destination });
      },
      error: (error) => {
        this.isSaving.set(false);
        this.error.set(
          error?.error?.message ||
            `Failed to ${this.isEditMode ? 'update' : 'create'} push destination`,
        );
      },
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
